import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, Loader2, TrendingUp, TrendingDown, Wallet,
  DollarSign, FileText, Clock, Scale, RefreshCw, Download,
  FileSpreadsheet
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BalanceSheet() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split("T")[0]);
  const [exporting, setExporting] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(null);
  const reportRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    loadBalanceSheet();
    loadBusinessProfile();
  }, [asOfDate]);

  const loadBusinessProfile = async () => {
    try {
      const response = await axios.get(`${API}/business-profile`);
      setBusinessProfile(response.data);
    } catch (error) {
      console.error("Failed to load business profile:", error);
    }
  };

  const loadBalanceSheet = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/balance-sheet?as_of_date=${asOfDate}`);
      setData(response.data);
    } catch (error) {
      toast.error("Failed to load balance sheet");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  };

  const exportToPDF = async () => {
    if (!reportRef.current || !data) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff"
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save(`balance-sheet-${asOfDate}.pdf`);
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to export PDF");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = () => {
    if (!data) return;
    
    const rows = [
      ["Balance Sheet"],
      [`As of ${formatDate(asOfDate)}`],
      [""],
      ["ASSETS"],
      ["Cash Balance", data.assets.cash_balance],
      ["Receivables", data.assets.receivables],
      ["Total Assets", data.assets.total_assets],
      [""],
      ["LIABILITIES"],
      ["Payables", data.liabilities.payables],
      ["Total Liabilities", data.liabilities.total_liabilities],
      [""],
      ["EQUITY"],
      ["Opening Balance", data.equity.opening_balance],
      ["Retained Earnings", data.equity.retained_earnings],
      ["Total Equity", data.equity.total_equity],
      [""],
      ["INCOME SUMMARY"],
      ["Total Income", data.income_summary.total_income],
      ["Total Expenses", data.income_summary.total_expenses],
      ["Net Income", data.income_summary.net_income],
      [""],
      ["Income by Category"],
      ...Object.entries(data.income_summary.income_by_category).map(([cat, amt]) => [cat, amt]),
      [""],
      ["Expenses by Category"],
      ...Object.entries(data.income_summary.expenses_by_category).map(([cat, amt]) => [cat, amt])
    ];
    
    const csv = rows.map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `balance-sheet-${asOfDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Excel/CSV downloaded");
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="balance-sheet-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Link to="/cash-flow">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">{t('cashFlow.balanceSheet')}</h1>
            <p className="text-sm text-muted-foreground">
              {formatDate(asOfDate)}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            className="w-40 h-9"
            data-testid="as-of-date"
          />
          <Button variant="ghost" size="sm" onClick={loadBalanceSheet} disabled={loading} className="h-9 w-9 p-0">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={exportToPDF} disabled={exporting || !data} className="h-9">
            <Download className="w-4 h-4 mr-1.5" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={exportToExcel} disabled={!data} className="h-9">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" />
            Excel
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : data ? (
        <div ref={reportRef} className="space-y-8 bg-white p-6 rounded-lg">
          {/* Company Branding Header - For PDF Export */}
          <div className="flex items-start justify-between pb-6 border-b-2" style={{ borderColor: businessProfile?.primary_color || '#047857' }}>
            <div className="flex items-center gap-4">
              {(businessProfile?.logo_data || businessProfile?.logo_url) && (
                <img 
                  src={businessProfile.logo_data || businessProfile.logo_url}
                  alt="Company Logo"
                  className="h-12 w-auto max-w-[100px] object-contain"
                  crossOrigin="anonymous"
                />
              )}
              <div>
                <h1 className="text-xl font-bold" style={{ color: businessProfile?.primary_color || '#047857' }}>
                  {businessProfile?.business_name || t('cashFlow.balanceSheet')}
                </h1>
                <p className="text-sm text-muted-foreground">{t('cashFlow.balanceSheet')}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium">{formatDate(asOfDate)}</p>
              <p className="text-xs text-muted-foreground">{new Date().toLocaleDateString()}</p>
            </div>
          </div>

          {/* Key Metrics Row */}
          <div className="flex items-baseline gap-12 pb-6 border-b border-border">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('cashFlow.totalInflows')}</p>
              <p className="text-3xl font-bold tabular-nums">{formatCurrency(data.assets.total_assets)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Liabilities</p>
              <p className="text-3xl font-bold tabular-nums text-red-500">{formatCurrency(data.liabilities.total_liabilities)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Equity</p>
              <p className={`text-3xl font-bold tabular-nums ${data.equity.total_equity >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {formatCurrency(data.equity.total_equity)}
              </p>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid md:grid-cols-2 gap-12">
            {/* Left Column - Assets */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                Assets
              </h2>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Cash Balance</span>
                  </div>
                  <span className="font-medium tabular-nums">{formatCurrency(data.assets.cash_balance)}</span>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Receivables</span>
                    {data.assets.receivables_count > 0 && (
                      <span className="text-xs text-muted-foreground">
                        ({data.assets.receivables_count} unpaid)
                      </span>
                    )}
                  </div>
                  <span className="font-medium tabular-nums">{formatCurrency(data.assets.receivables)}</span>
                </div>
                
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <span className="font-semibold">Total Assets</span>
                  <span className="font-bold text-lg tabular-nums">{formatCurrency(data.assets.total_assets)}</span>
                </div>
              </div>
            </div>

            {/* Right Column - Liabilities & Equity */}
            <div className="space-y-8">
              {/* Liabilities */}
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-red-500" />
                  Liabilities
                </h2>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Payables</span>
                      {data.liabilities.payables_count > 0 && (
                        <span className="text-xs text-muted-foreground">
                          ({data.liabilities.payables_count} pending)
                        </span>
                      )}
                    </div>
                    <span className="font-medium tabular-nums">{formatCurrency(data.liabilities.payables)}</span>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="font-semibold">Total Liabilities</span>
                    <span className="font-bold text-lg tabular-nums text-red-500">{formatCurrency(data.liabilities.total_liabilities)}</span>
                  </div>
                </div>
              </div>

              {/* Equity */}
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4 flex items-center gap-2">
                  <Scale className="w-4 h-4 text-blue-500" />
                  Equity
                </h2>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm">Opening Balance</span>
                    <span className="font-medium tabular-nums">{formatCurrency(data.equity.opening_balance)}</span>
                  </div>
                  
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <span className="text-sm">Retained Earnings</span>
                      <p className="text-xs text-muted-foreground">Net income this period</p>
                    </div>
                    <span className={`font-medium tabular-nums ${data.equity.retained_earnings >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {formatCurrency(data.equity.retained_earnings)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="font-semibold">Total Equity</span>
                    <span className={`font-bold text-lg tabular-nums ${data.equity.total_equity >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {formatCurrency(data.equity.total_equity)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Balance Check */}
          <div className={`flex items-center justify-between py-3 px-4 rounded-md ${
            data.balance_check.balanced 
              ? 'bg-emerald-50 dark:bg-emerald-900/10' 
              : 'bg-amber-50 dark:bg-amber-900/10'
          }`}>
            <div className="text-sm">
              <span className="font-medium">Balance Check: </span>
              <span className="text-muted-foreground">
                Assets ({formatCurrency(data.balance_check.total_assets)}) = Liabilities + Equity ({formatCurrency(data.balance_check.total_liabilities_and_equity)})
              </span>
            </div>
            <span className={`text-sm font-medium ${data.balance_check.balanced ? 'text-emerald-600' : 'text-amber-600'}`}>
              {data.balance_check.balanced ? '✓ Balanced' : '⚠ Unbalanced'}
            </span>
          </div>

          {/* Footnote */}
          <p className="text-xs text-muted-foreground text-center pt-4">
            Simplified cash-focused financial snapshot · Not for statutory reporting
          </p>
        </div>
      ) : (
        <div className="text-center py-16">
          <p className="text-muted-foreground">Failed to load data</p>
          <Button variant="outline" onClick={loadBalanceSheet} className="mt-4">
            Try Again
          </Button>
        </div>
      )}
    </div>
  );
}
